import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import * as signalR from '@microsoft/signalr';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import SupportTypingDots from '../../components/common/SupportTypingDots';
import { BRAND, brandAlpha } from '../../theme/brandTokens';
import { createSupportTypingReporter, isInboxOwnMessage, isInboxPeerTyping, sameSupportId, supportInboxTitle } from '../../utils/supportChatThread';
import { API_URL } from '../../config/runtimeConfig';
import { formatCalendarDate, formatClockTime, formatUtcDateTime } from '../../utils/dateFormatUtils';
import { useAuth } from '../../context/AuthContext';
import { useAppData } from '../../context/AppDataContext';
import { useStaffRealtime } from '../../context/StaffRealtimeContext';
import { useAcknowledgeOpenChat } from '../../hooks/useAcknowledgeOpenChat';
import {
  closeSupportConversation,
  getSupportConversation,
  getSupportConversations,
  sendAdminSupportMessage,
} from '../../services/supportChatService';

const resolveHubUrl = () => {
  const apiBase = (API_URL || '').replace(/\/$/, '');
  const origin = apiBase.replace(/\/api$/i, '');
  return `${origin}/hubs/support-chat`;
};

const statusColor = {
  Waiting: 'warning',
  Open: 'success',
  Closed: 'default',
};

const sameId = (left, right) => sameSupportId(left, right);

const SupportInboxPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const { managementUser } = useAuth();
  const { currentStore } = useAppData();
  const { acknowledgeChat } = useStaffRealtime();
  const isAppAdmin = managementUser?.userType === 1 || managementUser?.userType === '1' || managementUser?.userType === 'AppAdmin';
  const isStoreInbox = !isAppAdmin;
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  useAcknowledgeOpenChat(selectedId, acknowledgeChat);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [visitorTyping, setVisitorTyping] = useState(false);
  const [typingByConversationId, setTypingByConversationId] = useState({});
  const listRef = useRef(null);
  const selectedIdRef = useRef(null);
  const connectionRef = useRef(null);
  const sendTypingRef = useRef(() => undefined);
  const typingReporterRef = useRef(null);
  const typingTimeoutsRef = useRef({});
  const isStoreInboxRef = useRef(isStoreInbox);
  isStoreInboxRef.current = isStoreInbox;
  const currentStoreIdRef = useRef(currentStore?.id);
  currentStoreIdRef.current = currentStore?.id;
  selectedIdRef.current = selectedId;
  sendTypingRef.current = (isTyping) => {
    const connection = connectionRef.current;
    const id = selectedIdRef.current;
    if (!connection || !id || connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    connection.invoke('NotifyTyping', id, isTyping, null).catch(() => undefined);
  };

  if (!typingReporterRef.current) {
    typingReporterRef.current = createSupportTypingReporter({
      send: (isTyping) => sendTypingRef.current(isTyping),
    });
  }

  const loadInbox = useCallback(async () => {
    const items = await getSupportConversations();
    setConversations(items);
  }, []);

  const loadThread = useCallback(async (conversationId) => {
    const next = await getSupportConversation(conversationId);
    setThread(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadInbox()
      .catch(() => {
        if (!cancelled) {
          setError(t('supportChat.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadInbox, t]);

  const selectConversation = useCallback((conversationId) => {
    setSelectedId(conversationId);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (conversationId) {
        next.set('conversation', conversationId);
      } else {
        next.delete('conversation');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId) {
      setSelectedId(conversationId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!selectedId) {
      setThread(null);
      setVisitorTyping(false);
      typingReporterRef.current?.stop();
      return;
    }
    loadThread(selectedId).catch(() => setError(t('supportChat.loadFailed')));
  }, [selectedId, loadThread, t]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [thread?.messages, visitorTyping]);

  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(), {
        accessTokenFactory: () => localStorage.getItem('management_auth_token') || '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build();

    connection.on('ReceiveSupportMessage', (message) => {
      if (!selectedIdRef.current || !sameId(message?.conversationId, selectedIdRef.current)) {
        return;
      }
      if (isInboxPeerTyping(message?.senderKind, isStoreInboxRef.current)) {
        setVisitorTyping(false);
        setTypingByConversationId((current) => {
          const next = { ...current };
          delete next[String(message.conversationId).toLowerCase()];
          return next;
        });
      }
      setThread((current) => {
        if (!current || current.messages.some((item) => sameId(item.id, message.id))) {
          return current;
        }
        return { ...current, messages: [...current.messages, message] };
      });
    });

    connection.on('SupportConversationChanged', () => {
      loadInbox().catch(() => undefined);
      if (selectedIdRef.current) {
        loadThread(selectedIdRef.current).catch(() => undefined);
      }
    });

    connection.on('SupportTyping', (payload) => {
      if (!isInboxPeerTyping(payload?.senderKind, isStoreInboxRef.current) || !payload?.conversationId) {
        return;
      }
      const conversationKey = String(payload.conversationId).toLowerCase();
      const isTyping = Boolean(payload.isTyping);
      if (typingTimeoutsRef.current[conversationKey]) {
        clearTimeout(typingTimeoutsRef.current[conversationKey]);
        delete typingTimeoutsRef.current[conversationKey];
      }
      setTypingByConversationId((current) => {
        const next = { ...current };
        if (isTyping) {
          next[conversationKey] = true;
        } else {
          delete next[conversationKey];
        }
        return next;
      });
      if (sameId(payload.conversationId, selectedIdRef.current)) {
        setVisitorTyping(isTyping);
      }
      if (!isTyping) {
        return;
      }
      typingTimeoutsRef.current[conversationKey] = setTimeout(() => {
        delete typingTimeoutsRef.current[conversationKey];
        setTypingByConversationId((current) => {
          if (!current[conversationKey]) {
            return current;
          }
          const next = { ...current };
          delete next[conversationKey];
          return next;
        });
        if (sameId(payload.conversationId, selectedIdRef.current)) {
          setVisitorTyping(false);
        }
      }, 5000);
    });

    connectionRef.current = connection;
    connection.start()
      .then(async () => {
        await connection.invoke('JoinInbox', isStoreInboxRef.current ? currentStoreIdRef.current : null);
        if (selectedIdRef.current) {
          await connection.invoke('JoinConversation', selectedIdRef.current, null);
        }
      })
      .catch(() => undefined);

    return () => {
      Object.values(typingTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
      typingTimeoutsRef.current = {};
      connection.stop().catch(() => undefined);
      connectionRef.current = null;
    };
  }, [loadInbox, loadThread]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!isStoreInbox || !currentStore?.id || !connection || connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    connection.invoke('JoinInbox', currentStore.id).catch(() => undefined);
  }, [isStoreInbox, currentStore?.id]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || !selectedId || connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    connection.invoke('JoinConversation', selectedId, null).catch(() => undefined);
  }, [selectedId]);

  const handleSend = async () => {
    if (!selectedId || !draft.trim()) {
      return;
    }
    typingReporterRef.current?.stop();
    setSending(true);
    setError(null);
    try {
      const message = await sendAdminSupportMessage(selectedId, draft.trim());
      setDraft('');
      setThread((current) => current
        ? { ...current, status: 'Open', messages: [...current.messages, message] }
        : current);
      await loadInbox();
    } catch (sendError) {
      setError(sendError?.response?.data?.message || t('supportChat.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!selectedId) {
      return;
    }
    await closeSupportConversation(selectedId);
    await loadInbox();
    await loadThread(selectedId);
  };

  return (
    <ManagementPageLayout
      pageTitle={t(isStoreInbox ? 'supportChat.storeInbox' : 'supportChat.inbox')}
      pageSubtitle={t(isStoreInbox ? 'supportChat.storeInboxSubtitle' : 'supportChat.inboxSubtitle')}
    >
      <ManagementPageCard
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: { xs: 'calc(100dvh - 220px)', md: 560 },
          height: { xs: 'calc(100dvh - 220px)', md: 'min(720px, calc(100vh - 200px))' },
        }}
      >
        <Box
          sx={{
            display: { xs: selectedId ? 'none' : 'block', md: 'block' },
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            overflowY: 'auto',
            minHeight: 0,
            borderRight: { md: `1px solid ${BRAND.line}` },
          }}
        >
          {loading ? (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} sx={{ color: BRAND.accent }} />
            </Box>
          ) : conversations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              {t(isStoreInbox ? 'supportChat.storeNoConversations' : 'supportChat.noConversations')}
            </Typography>
          ) : (
            <List disablePadding>
              {conversations.map((item) => (
                <ListItemButton
                  key={item.id}
                  selected={sameId(item.id, selectedId)}
                  onClick={() => selectConversation(item.id)}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body1" component="span">
                          {supportInboxTitle({
                            isStoreInbox,
                            storeName: item.storeName,
                            initiatorDisplayName: item.initiatorDisplayName,
                            storeFallback: t('supportChat.store'),
                          })}
                        </Typography>
                        {item.isPrioritySupport || item.IsPrioritySupport ? (
                          <Chip
                            size="small"
                            label={t('storePlan.prioritySupportBadge')}
                            sx={{
                              height: 20,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              bgcolor: brandAlpha('accent', 0.12),
                              color: BRAND.accentText,
                            }}
                          />
                        ) : null}
                      </Box>
                    )}
                    secondary={(
                      <>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {item.initiatorEmail}
                        </Typography>
                        <Typography variant="body2" noWrap sx={typingByConversationId[String(item.id).toLowerCase()] ? { color: BRAND.accentText, fontWeight: 600 } : undefined}>
                          {typingByConversationId[String(item.id).toLowerCase()]
                            ? t('supportChat.typing')
                            : item.lastMessagePreview}
                        </Typography>
                      </>
                    )}
                  />
                  <Chip
                    size="small"
                    color={statusColor[item.status] || 'default'}
                    label={t(`supportChat.status.${item.status}`)}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: { xs: selectedId ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {!thread ? (
            <Box sx={{ p: 4 }}>
              <Typography color="text.secondary">{t('supportChat.selectConversation')}</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between',
                  gap: { xs: 1.5, md: 2 },
                  alignItems: { xs: 'stretch', md: 'flex-start' },
                }}
              >
                {isCompact ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <IconButton
                      onClick={() => selectConversation(null)}
                      aria-label={t('common.back')}
                      sx={{ color: BRAND.accentText }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    {thread.status !== 'Closed' ? (
                      <PrimaryActionButton onClick={handleClose} sx={{ height: 40, flexShrink: 0 }}>
                        {t('supportChat.closeConversation')}
                      </PrimaryActionButton>
                    ) : null}
                  </Box>
                ) : null}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {supportInboxTitle({
                      isStoreInbox,
                      storeName: thread.storeName,
                      initiatorDisplayName: thread.initiatorDisplayName,
                      storeFallback: t('supportChat.store'),
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{thread.initiatorEmail}</Typography>
                  {isStoreInbox && thread.storeName ? (
                    <Typography variant="caption" color="text.secondary">{thread.storeName}</Typography>
                  ) : null}
                  {thread.relatedAppointment ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <RouterLink
                        to={`/management/appointments?date=${String(thread.relatedAppointment.date).slice(0, 10)}&appointment=${thread.relatedAppointment.id}`}
                        style={{ color: BRAND.accentText, fontWeight: 600 }}
                      >
                        {t('supportChat.relatedAppointment', {
                          date: formatCalendarDate(thread.relatedAppointment.date),
                          time: formatClockTime(thread.relatedAppointment.start),
                        })}
                      </RouterLink>
                    </Typography>
                  ) : null}
                </Box>
                {!isCompact && thread.status !== 'Closed' ? (
                  <PrimaryActionButton onClick={handleClose} sx={{ height: 40, flexShrink: 0 }}>
                    {t('supportChat.closeConversation')}
                  </PrimaryActionButton>
                ) : null}
              </Box>
              <Divider />
              <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0, px: 2.5, py: 2, bgcolor: BRAND.background }}>
                {(thread.messages || []).map((message) => {
                  const isOwn = isInboxOwnMessage(message.senderKind, isStoreInbox);
                  return (
                    <Box key={message.id} sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', mb: 1 }}>
                      <Box sx={{
                        maxWidth: '75%',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: isOwn ? BRAND.accent : BRAND.surface,
                        color: isOwn ? BRAND.onAccent : BRAND.text,
                        border: isOwn ? 'none' : `1px solid ${BRAND.lineSoft}`,
                      }}>
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
                          {isOwn
                            ? t(isStoreInbox ? 'supportChat.store' : 'supportChat.support')
                            : supportInboxTitle({
                              isStoreInbox,
                              storeName: thread.storeName,
                              initiatorDisplayName: thread.initiatorDisplayName,
                              storeFallback: t('supportChat.store'),
                            })}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message.body}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, mt: 0.5 }}>
                          {formatUtcDateTime(message.createdAtUtc)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                {visitorTyping ? (
                  <SupportTypingDots label={t('supportChat.visitorTyping', {
                    name: supportInboxTitle({
                      isStoreInbox,
                      storeName: thread.storeName,
                      initiatorDisplayName: thread.initiatorDisplayName,
                      storeFallback: t('supportChat.store'),
                    }),
                  })} />
                ) : null}
              </Box>
              {error ? (
                <Typography variant="caption" color="error" sx={{ px: 2.5, pt: 1 }}>{error}</Typography>
              ) : null}
              {thread.status === 'Closed' ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>
                  {t('supportChat.closed')}
                </Typography>
              ) : (
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={draft}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraft(value);
                      if (thread.status === 'Closed') {
                        return;
                      }
                      if (value.trim()) {
                        typingReporterRef.current?.ping();
                      } else {
                        typingReporterRef.current?.stop();
                      }
                    }}
                    placeholder={t('supportChat.placeholder')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        handleSend();
                      }
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        height: 48,
                      },
                    }}
                  />
                  <PrimaryActionButton
                    onClick={handleSend}
                    disabled={sending || !draft.trim()}
                    sx={{ flexShrink: 0, alignSelf: 'center' }}
                  >
                    {t('supportChat.send')}
                  </PrimaryActionButton>
                </Box>
              )}
            </>
          )}
        </Box>
      </ManagementPageCard>
    </ManagementPageLayout>
  );
};

export default SupportInboxPage;
